// offline-answer.service.ts
import { Injectable } from '@angular/core';
import { ExamineeService } from './examinee.service';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OfflineAnswerService {
  private offlineAnswers: any[] = [];
  private retryIntervalSeconds = 5;
  private retrySubscription: Subscription | null = null;
  private isRetryingSubject = new BehaviorSubject<boolean>(false);
  isRetrying$ = this.isRetryingSubject.asObservable();

	private _hasPendingAnswers$ = new BehaviorSubject<boolean>(false);
  hasPendingAnswers$ = this._hasPendingAnswers$.asObservable();

  constructor(private examineeService: ExamineeService) {
		this._hasPendingAnswers$.next(this.offlineAnswers.length > 0);
	}

  addFailedAnswer(answer: any): void {
    this.offlineAnswers.push(answer);
		this._hasPendingAnswers$.next(true);
    this.startRetryMechanism();
    console.error('Answer saving failed. Will retry when online.', 'Offline Mode');
  }

  private startRetryMechanism(): void {
    if (!this.retrySubscription) {
      this.retrySubscription = interval(this.retryIntervalSeconds * 1000)
        .pipe(
          tap(() => this.isRetryingSubject.next(true)),
          switchMap(() => {
            if (this.offlineAnswers.length > 0) {
              return this.examineeService.saveAnswer(this.offlineAnswers[0]).pipe(
                tap(() => {
                  this.offlineAnswers.shift(); // Remove the successfully saved answer
                  this._hasPendingAnswers$.next(this.offlineAnswers.length > 0); // Update the subject
                  if (this.offlineAnswers.length === 0) {
                    this.stopRetryMechanism();
                    console.info('All pending answers saved successfully!', 'Back Online');
                  }
                }),
                catchError((error) => {
                  this.isRetryingSubject.next(false);
                  console.error('Error retrying answer:', error);
                  return []; // Continue the interval
                })
              );
            } else {
              this.stopRetryMechanism(); // Stop if no more offline answers
              return [];
            }
          }),
          tap(() => this.isRetryingSubject.next(false))
        )
        .subscribe();
    }
  }

  private stopRetryMechanism(): void {
    if (this.retrySubscription) {
      this.retrySubscription.unsubscribe();
      this.retrySubscription = null;
      this.isRetryingSubject.next(false);
    }
  }

  hasPendingAnswers(): boolean {
    return this.offlineAnswers.length > 0;
  }

  pendingAnswers(): number {
    return this.offlineAnswers.length;
  }
}